import { isEqual, sortBy, groupBy } from "lodash-es";
import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import Auth from "shared/@auth";
import createNotification from "shared/@utils/createNotification";
import normalizeCategory from "../normalizeCategory";

/**
 * Sorts categories by parent and stepNumber and updates stepNumber
 * to be sequential. This fixes issues where the items are in the right order
 * but the numbers are out of wack, or where the items have no numbering at all.
 *
 * NOTE: This step is essential for cleaning the data to avoid problems with sorting
 *
 * @param {category[]} categories - array of categories from the API
 */
function renumberCategories(categories) {
  const renumbered = [];

  const sorted = sortBy(
    categories,
    "parentCategoryID",
    "rowNumber",
    "stepNumber"
  );
  const byParent = groupBy(sorted, (record) => record.parentCategoryID);

  // re-number items in sort order
  for (const parentId in byParent) {
    let stepNumber = 1;

    const updated = sortBy(byParent[parentId], "rowNumber", "stepNumber").map(
      (category) => {
        return {
          ...category,

          // set stepNumber to next inc if published, otherwise set drafts to 0
          stepNumber: category.isPublished ? stepNumber++ : 0,

          // set row number on all subcategories
          rowNumber: category.rowNumber || 1,
        };
      }
    );

    renumbered.push(...updated);
  }

  // console.log(renumbered);

  return sortBy(renumbered, "id");
}

/**
 * Generic data hook for application categories. Right now this is Retail and Digital, but could include other apps in the future.
 */
export function useAppCategoriesData(appId) {
  if (appId == null) {
    throw new Error(`Must pass a non-null application ID`);
  }

  const { makeAuthedRequest } = Auth.useContainer();

  const queryClient = useQueryClient();
  const [categories, setCategories] = useState([]);
  const [originals, setOriginals] = useState([]);
  const serverSnapshot = useRef({ appId, categories: [] });

  const categoriesQuery = useQuery(["categories", appId], () =>
    makeAuthedRequest(`/category-builder/categories/${appId}`).then(
      async (res) => {
        const json = await res.json();

        if (res.ok) {
          return sortBy(json.map(normalizeCategory), "id");
        } else {
          throw new Error(json.message);
        }
      }
    )
  );

  useEffect(() => {
    if (categoriesQuery.isSuccess) {
      const data = renumberCategories(categoriesQuery.data);

      const previousSnapshot = serverSnapshot.current;
      serverSnapshot.current = { appId, categories: data };
      setCategories((currentCategories) => {
        if (previousSnapshot.appId !== appId) return data;

        const previousById = new Map(
          previousSnapshot.categories.map((category) => [category.id, category])
        );
        const currentById = new Map(
          currentCategories.map((category) => [category.id, category])
        );
        const incomingIds = new Set(data.map((category) => category.id));
        const merged = data
          .filter(
            (category) =>
              !previousById.has(category.id) || currentById.has(category.id)
          )
          .map((category) => {
            const current = currentById.get(category.id);
            const previous = previousById.get(category.id);
            return current && previous && !isEqual(current, previous)
              ? { ...category, ...current }
              : category;
          });
        const addedLocally = currentCategories.filter(
          (category) =>
            !previousById.has(category.id) && !incomingIds.has(category.id)
        );
        return renumberCategories([...merged, ...addedLocally]);
      });
      setOriginals(data);
    }
  }, [appId, categoriesQuery.isSuccess, categoriesQuery.data]);

  const addCategoryMutation = useMutation(
    (category) =>
      makeAuthedRequest(`/category-builder/categories`, {
        method: "post",
        body: JSON.stringify({
          ...category,
          applicationTypeID: appId,
        }),
      }).then(async (res) => {
        const json = await res.json();

        if (res.ok) {
          return json;
        } else {
          throw new Error(json.message);
        }
      }),
    {
      onSuccess: async () => {
        queryClient.invalidateQueries(["cms"]);
        await queryClient.invalidateQueries(["categories", appId]);

        createNotification({
          type: "success",
          title: "Added",
          message: "New category added",
          autoDismiss: true,
        });
      },
    }
  );

  const saveMutation = useMutation(
    async () => {
      const added = [];
      const removed = [];
      const updated = [];

      for (const category of categories) {
        // no id means it hasn't been saved yet
        if (category.id == null) {
          added.push(category);
          continue;
        }

        const original = originals.find((o) => o.id === category.id);

        // no original means this one is new
        if (!original) {
          added.push(category);
          continue;
        }

        // deep compare to find out if we need to make an API call
        if (!isEqual(original, category)) {
          updated.push(category);
          continue;
        }
      }

      for (const category of originals) {
        const match = categories.find((c) => c.id === category.id);

        // without a match in the current list means this one has been removed
        if (!match) {
          removed.push(category);
          continue;
        }
      }

      if (categories !== originals) {
        await makeAuthedRequest(`/category-builder/steps`, {
          method: "put",
          body: JSON.stringify(
            categories.map((c) => ({ id: c.id, stepNumber: c.stepNumber }))
          ),
        });
      }

      const requests = [
        ...added.map((category) =>
          makeAuthedRequest(`/category-builder/categories`, {
            method: "post",
            body: JSON.stringify(category),
          })
        ),
        ...updated.map((category) =>
          makeAuthedRequest(`/category-builder/categories`, {
            method: "put",
            body: JSON.stringify(category),
          })
        ),
        ...removed.map((category) =>
          makeAuthedRequest(`/category-builder/categories/${category.id}`, {
            method: "delete",
          })
        ),
      ];

      return Promise.all(requests);
    },
    {
      onSuccess: () => {
        // Have to blip all CMS data because it comes down together in one request
        queryClient.invalidateQueries(["cms"]);
        queryClient.invalidateQueries(["categories", appId]);

        createNotification({
          type: "success",
          title: "Saved",
          message: "Category changes were successfully saved",
          autoDismiss: true,
        });
      },
    }
  );

  return {
    isChanged: !isEqual(categories, originals),
    isSuccess: categoriesQuery.isSuccess,
    isLoading: categoriesQuery.isLoading,
    isError: categoriesQuery.isError,
    categories: categories,
    error: categoriesQuery.error,
    reset: () => setCategories(originals),

    setCategories,
    saveMutation,
    addCategoryMutation,
  };
}
